import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';

/*
 * HOOK DE PAGINATION SANS PROBLÈME D'INDEX FIRESTORE
 * 
 * STRATÉGIE :
 * - Requêtes Firestore optimisées avec filtrage côté serveur
 * - Filtrage côté client minimal pour éviter les index composites
 * - Même logique que NotificationsOverview pour la cohérence
 */

const PAGE_SIZE = 20;

export const useNotificationsPagination = (filters = {}, isMessagerieMode = false) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  
  const { user } = useAuth();
  const unsubscribeRef = useRef(null);
  const forceRefreshCounter = useRef(0);

  console.log('🔍 PAGINATION-HOOK: Initialisation', {
    isMessagerieMode,
    userId: user?.uid,
    filters
  });

  // Fonction de nettoyage
  const cleanup = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  };

  // Fonction de chargement des données avec requête optimisée
  const loadNotifications = (isLoadMore = false) => {
    if (!user) {
      setNotifications([]);
      setInitialLoading(false);
      return;
    }

    setLoading(true);
    if (!isLoadMore) {
      setInitialLoading(true);
      setError(null);
    }

    console.log('🔍 PAGINATION-HOOK: Chargement', { isLoadMore, collection: isMessagerieMode ? 'discussions' : 'notifications' });

    try {
      cleanup();

      const collectionName = isMessagerieMode ? 'discussions' : 'notifications';
      
      // ✅ REQUÊTE FIRESTORE OPTIMISÉE - Filtrer côté serveur selon le readStatus
      let q;
      
      if (isMessagerieMode) {
        // DISCUSSIONS : Garder requête simple (filtrage côté client nécessaire)
        q = query(
          collection(db, collectionName),
          orderBy('createdAt', 'desc')
        );
      } else {
        // NOTIFICATIONS : Adapter la requête selon le filtre readStatus
        if (filters.readStatus === 'unread') {
          // Filtre UNREAD : Récupérer directement les non lues côté serveur
          q = query(
            collection(db, collectionName),
            where('userId', '==', user.uid),
            where('lu', '==', false),
            orderBy('createdAt', 'desc')
          );
        } else if (filters.readStatus === 'read') {
          // Filtre READ : Récupérer directement les lues côté serveur
          q = query(
            collection(db, collectionName),
            where('userId', '==', user.uid),
            where('lu', '==', true),
            orderBy('createdAt', 'desc')
          );
        } else {
          // Filtre ALL : Récupérer toutes les notifications de l'utilisateur
          q = query(
            collection(db, collectionName),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc')
          );
        }
      }

      // Pagination
      if (isLoadMore && lastVisible) {
        q = query(q, startAfter(lastVisible), limit(PAGE_SIZE));
      } else {
        q = query(q, limit(PAGE_SIZE));
      }

      console.log('🔍 PAGINATION-HOOK: Requête créée pour', collectionName);

      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log('📡 PAGINATION-HOOK: Snapshot reçu avec', snapshot.size, 'documents');

        let newNotifications = [];
        let lastDoc = null;
        let totalProcessed = 0;
        let passedUserIdFilter = 0;

        snapshot.forEach((doc) => {
          const data = { id: doc.id, ...doc.data() };
          totalProcessed++;
          
          // FILTRAGE CÔTÉ CLIENT selon le mode
          let shouldInclude = false;
          
          if (isMessagerieMode) {
            // DISCUSSIONS : L'utilisateur doit être participant
            const isParticipant = data.participants && data.participants.includes(user.uid);
            if (isParticipant) {
              shouldInclude = true;
              passedUserIdFilter++;
              console.log('📄 PAGINATION-HOOK: Discussion incluse (participant):', data.title);
            } else {
              console.log('❌ PAGINATION-HOOK: Discussion exclue (pas participant):', data.title);
            }
          } else {
            // ✅ NOTIFICATIONS : Déjà filtrées côté serveur par userId et readStatus, toutes sont valides
            shouldInclude = true;
            passedUserIdFilter++;
            console.log('📄 PAGINATION-HOOK: Notification incluse (filtrée côté serveur):', data.title);
          }

          if (shouldInclude) {
            // APPLIQUER LES FILTRES CÔTÉ CLIENT RESTANTS
            let passesFilters = true;

            // 🔍 DEBUG: Logs pour comprendre les filtres
            console.log('🔍 PAGINATION: Filtres appliqués:', filters);
            console.log('🔍 PAGINATION: Notification à analyser:', {
              title: data.title,
              userId: data.userId,
              lu: data.lu,
              read: data.read,
              deleted: data.deleted,
              archived: data.archived,
              projectId: data.projectId
            });

            // Filtre par statut de lecture (pour les discussions seulement, notifications déjà filtrées côté serveur)
            if (filters.readStatus && filters.readStatus !== 'all' && isMessagerieMode) {
              console.log('🔍 PAGINATION: Filtre readStatus appliqué pour discussion:', filters.readStatus);
              
              const isRead = data.readBy && data.readBy.includes(user.uid);
              
              if (filters.readStatus === 'read' && !isRead) {
                passesFilters = false;
                console.log('❌ PAGINATION: Discussion exclue (readStatus=read mais discussion non lue)');
              } else if (filters.readStatus === 'unread' && isRead) {
                passesFilters = false;
                console.log('❌ PAGINATION: Discussion exclue (readStatus=unread mais discussion lue)');
              } else {
                console.log('✅ PAGINATION: Discussion passe le filtre readStatus');
              }
            } else {
              console.log('🔍 PAGINATION: Filtre readStatus géré côté serveur ou non applicable');
            }

            // Filtre par projet
            if (filters.project && filters.project !== 'all' && data.projectId !== filters.project) {
              passesFilters = false;
              console.log('❌ PAGINATION: Notification exclue (projet différent):', data.projectId, 'vs', filters.project);
            }

            // Filtre par statut supprimé/archivé (côté client)
            if (filters.deleted === true) {
              if (!data.deleted) {
                passesFilters = false;
                console.log('❌ PAGINATION: Notification exclue (filtre deleted=true mais notification non supprimée)');
              }
            } else {
              if (data.deleted) {
                passesFilters = false;
                console.log('❌ PAGINATION: Notification exclue (notification supprimée)');
              }
              if (data.archived && !filters.showArchived) {
                passesFilters = false;
                console.log('❌ PAGINATION: Notification exclue (notification archivée)');
              }
            }

            // Filtres de date
            if (filters.startDate && data.createdAt) {
              const createdDate = data.createdAt.toDate();
              const startDate = new Date(filters.startDate);
              if (createdDate < startDate) {
                passesFilters = false;
                console.log('❌ PAGINATION: Notification exclue (date trop ancienne)');
              }
            }

            if (filters.endDate && data.createdAt) {
              const createdDate = data.createdAt.toDate();
              const endDate = new Date(filters.endDate);
              endDate.setHours(23, 59, 59, 999);
              if (createdDate > endDate) {
                passesFilters = false;
                console.log('❌ PAGINATION: Notification exclue (date trop récente)');
              }
            }

            if (passesFilters) {
              newNotifications.push(data);
              lastDoc = doc;
              console.log('✅ PAGINATION: Notification FINALE incluse:', data.title);
            } else {
              console.log('❌ PAGINATION: Notification FINALE exclue après filtrage:', data.title);
            }
          }
        });

        console.log('🔍 PAGINATION: Statistiques de filtrage:', {
          totalProcessed,
          passedUserIdFilter,
          finalCount: newNotifications.length,
          filters
        });
        console.log('📊 PAGINATION-HOOK: Notifications filtrées côté client:', newNotifications.length);

        if (isLoadMore) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }

        setLastVisible(lastDoc);
        setHasMore(newNotifications.length === PAGE_SIZE && lastDoc !== null);
        setLoading(false);
        setInitialLoading(false);
        setError(null);

      }, (err) => {
        console.error('❌ PAGINATION-HOOK: Erreur Firestore:', err);
        setError('Erreur de connexion temps réel: ' + err.message);
        setLoading(false);
        setInitialLoading(false);
      });

      unsubscribeRef.current = unsubscribe;

    } catch (err) {
      console.error('❌ PAGINATION-HOOK: Erreur lors de la création de la requête:', err);
      setError('Erreur lors du chargement: ' + err.message);
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    console.log('🔄 PAGINATION-HOOK: useEffect déclenché', {
      userId: user?.uid,
      forceRefreshCounter: forceRefreshCounter.current,
      filters
    });
    
    loadNotifications(false);
    
    return cleanup;
  }, [user?.uid, isMessagerieMode, forceRefreshCounter.current, JSON.stringify(filters)]);

  // Fonction pour charger plus de notifications
  const loadMoreNotifications = () => {
    if (!loading && hasMore) {
      console.log('📄 PAGINATION-HOOK: Chargement de plus de notifications...');
      loadNotifications(true);
    }
  };

  // Fonction pour mettre à jour les filtres
  const updateFilters = (newFilters) => {
    console.log('🔄 PAGINATION-HOOK: Mise à jour des filtres:', newFilters);
    setLastVisible(null);
    setHasMore(true);
    forceRefreshCounter.current = Date.now();
  };

  // Fonction pour rafraîchir
  const refreshNotifications = () => {
    console.log('🔄 PAGINATION-HOOK: Rafraîchissement forcé');
    setLastVisible(null);
    setHasMore(true);
    forceRefreshCounter.current = Date.now();
  };

  // Fonction pour mettre à jour les notifications (pour les mises à jour optimistes)
  const setNotificationsWrapper = (updater) => {
    if (typeof updater === 'function') {
      setNotifications(updater);
    } else {
      setNotifications(updater);
    }
  };

  // Écouter les événements de rafraîchissement
  useEffect(() => {
    const handleRefreshEvent = (event) => {
      console.log('🔄 PAGINATION-HOOK: Événement de rafraîchissement reçu:', event.type);
      refreshNotifications();
    };

    window.addEventListener('refreshNotifications', handleRefreshEvent);
    window.addEventListener('forceRefreshTimeline', handleRefreshEvent);

    return () => {
      window.removeEventListener('refreshNotifications', handleRefreshEvent);
      window.removeEventListener('forceRefreshTimeline', handleRefreshEvent);
    };
  }, []);

  return {
    notifications,
    loading,
    initialLoading,
    error,
    hasMore,
    loadMoreNotifications,
    updateFilters,
    refreshNotifications,
    setNotifications: setNotificationsWrapper
  };
};